//src\components\portal\portal_components\data_manager\data_manager.js
import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  Snackbar,
  Divider
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import portalSession from '../../portal_services/portal_session';

// Import our new components
import DataStats from './data_stats';
import DataControls from './data_controls';
import DataTable from './data_table';

const DataManager = () => {
  const theme = useTheme();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Track operations for saving
  const [pendingOperations, setPendingOperations] = useState([]);

  // API configuration
  const BASE_URL = portalSession.getBaseURL();
  const CUSTOMER_ID = portalSession.getCustomerId();
  const CUSTOMER_TOKEN = portalSession.getCustomerToken();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch existing pending operations FIRST
        let existingOperations = [];
        try {
          const operationsResponse = await fetch(`${BASE_URL}/get_link_operations`, {
            method: 'GET',
            headers: {
              'X-Customer-ID': CUSTOMER_ID,
              'X-Customer-Token': CUSTOMER_TOKEN,
              'Content-Type': 'application/json',
            },
          });
  
          if (operationsResponse.ok) {
            const operationsData = await operationsResponse.json();
            existingOperations = operationsData || [];
            setPendingOperations(existingOperations);
          }
        } catch (err) {
          console.warn('Could not fetch existing operations:', err);
        }
        
        // Fetch current pages
        const pagesResponse = await fetch(`${BASE_URL}/get_pages`, {
          method: 'GET',
          headers: {
            'X-Customer-ID': CUSTOMER_ID,
            'X-Customer-Token': CUSTOMER_TOKEN,
            'Content-Type': 'application/json',
          },
        });
  
        if (!pagesResponse.ok) {
          throw new Error(`HTTP error! status: ${pagesResponse.status}`);
        }
  
        const pagesData = await pagesResponse.json();
        
        // Add type field and check for existing remove operations
        const pagesWithType = pagesData.map(page => {
          const pageType = determinePageType(page);
          // Check if this page has a pending remove operation
          const hasRemoveOperation = existingOperations.some(op => 
            op.operation === 'remove' && op.url === page.page_link
          );
          
          return {
            ...page,
            type: pageType,
            isRemoved: hasRemoveOperation
          };
        });
        
        setPages(pagesWithType);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);

  // Helper function to determine page type
  const determinePageType = (page) => {
    const url = page.page_link.toLowerCase();
    const docExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf'];
    
    if (docExtensions.some(ext => url.includes(ext))) {
      return 'doc';
    }
    return 'site';
  };

  // Filter pages based on search term and type filter
  const filteredPages = pages.filter(page => {
    const matchesSearch = page.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.page_link.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || page.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  // Calculate stats
  // const stats = {
  //   total: pages.length,
  //   sites: pages.filter(p => p.type === 'site').length,
  //   docs: pages.filter(p => p.type === 'doc').length,
  //   toRemove: pendingOperations.filter(op => op.operation === 'remove').length,
  //   toAdd: pendingOperations.filter(op => op.operation === 'add').length
  // };
  const stats = {
    total: pages.length,
    sites: pages.filter(p => p.type === 'site').length,
    docs: pages.filter(p => p.type === 'doc').length,
    toRemove: pendingOperations.filter(op => op.operation === 'remove').length,
    toAdd: pendingOperations.filter(op => op.operation === 'add').length
  };

  const handleRemovePage = (pageIndex) => {
    const updatedPages = [...pages];
    updatedPages[pageIndex].isRemoved = !updatedPages[pageIndex].isRemoved;
    setPages(updatedPages);

    // Add to pending operations
    const page = updatedPages[pageIndex];
    const operation = {
      operation: page.isRemoved ? 'remove' : 'add',
      type: page.type,
      url: page.page_link,
      timestamp: new Date().toISOString()
    };

    setPendingOperations(prev => {
      // Remove any existing operation for this URL
      const filtered = prev.filter(op => op.url !== page.page_link);
      // Only add operation if it's a removal
      return page.isRemoved ? [...filtered, operation] : filtered;
    });
  };

  const handleAddUrl = (operation) => {
    setPendingOperations(prev => [...prev, operation]);
  };

  const handleRemovePendingOperation = (index) => {
    setPendingOperations(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
  
      // Include removal operations for marked pages
      const removalOperations = pages
        .filter(page => page.isRemoved && !pendingOperations.some(op => 
          op.operation === 'remove' && op.url === page.page_link
        ))
        .map(page => ({
          operation: 'remove',
          type: page.type,
          url: page.page_link,
          timestamp: new Date().toISOString()
        }));
  
      const allOperations = [...pendingOperations, ...removalOperations];
  
      if (allOperations.length === 0) {
        setError('No changes to save');
        setSaving(false);
        return;
      }
  
      const payload = {
        customerName: 'canterbury_basketball',
        linkOperations: allOperations
      };
  
      const response = await fetch(`${BASE_URL}/upload_link_selection`, {
        method: 'POST',
        headers: {
          'X-Customer-ID': CUSTOMER_ID,
          'X-Customer-Token': CUSTOMER_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccessMessage('Link selections saved successfully!');
        // Refresh data to get the latest state
        //window.location.reload(); // Or re-fetch data instead of reload
      } else {
        throw new Error(result.message || 'Failed to save link selections');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving link selections:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress color="primary" />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error && pages.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">
            Error loading pages: {error}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Card 
          elevation={3}
          sx={{ 
            backgroundColor: 'white',
            border: '2px solid #00796b',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: '#00796b',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3
              }}
            >
              <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Data Manager
            </Typography>

            <DataStats stats={stats} />
            
            <Divider sx={{ mb: 3 }} />

            <DataControls
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              onAddUrl={handleAddUrl}
              setError={setError}
            />

            <DataTable
              filteredPages={filteredPages}
              pendingOperations={pendingOperations}
              saving={saving}
              onRemovePage={handleRemovePage}
              onSave={handleSave}
              onRemovePendingOperation={handleRemovePendingOperation}
              stats={stats}
            />
          </CardContent>
        </Card>

        {/* Success/Error Snackbars */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccessMessage('')} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setError('')} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default DataManager;